import * as React from 'react';
import { Check, ChevronsUpDown, Search, User, Users, X, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Resource } from '@/types';
import { ResourceSearchModal } from './ResourceSearchModal';

interface EnhancedMultiResourceSelectProps {
  resources: Resource[];
  selectedResourceIds: string[];
  onSelect: (resourceIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  calculateUtilization?: (resourceId: string) => number;
}

function getInitials(name: string): string {
  return String(name ?? '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Deterministic seeded util so it's stable across renders
function seededUtil(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h % 101); // 0–100
}

// Returns the pastel tier colour as a hex string — used for bar fills and badge backgrounds
function getUtilHexColor(u: number): string {
  if (u >= 101) return '#fecaca'; // red-200   — Overloaded
  if (u >= 85)  return '#fde68a'; // amber-200 — Near Capacity
  if (u >= 70)  return '#bbf7d0'; // green-200 — On Track
  return '#bfdbfe';               // blue-200  — Available
}

// Returns a dark 700-level colour for text labels — readable on white/light backgrounds
function getUtilDarkColor(u: number): string {
  if (u >= 101) return '#b91c1c'; // red-700   — Overloaded
  if (u >= 85)  return '#b45309'; // amber-700 — Near Capacity
  if (u >= 70)  return '#047857'; // emerald-700 — On Track
  return '#1d4ed8';               // blue-700  — Available
}

function getUtilLabel(u: number): string {
  if (u >= 101) return 'Overloaded';
  if (u >= 85)  return 'Near Capacity';
  if (u >= 70)  return 'On Track';
  return 'Available';
}

export function EnhancedMultiResourceSelect({
  resources,
  selectedResourceIds,
  onSelect,
  disabled = false,
  placeholder = 'Select resources...',
  className,
  calculateUtilization,
}: EnhancedMultiResourceSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const selectedResources = React.useMemo(
    () => resources.filter((r) => selectedResourceIds.includes(String(r.id))),
    [resources, selectedResourceIds]
  );

  const filteredResources = React.useMemo(() => {
    if (!searchQuery) return resources;
    const q = searchQuery.toLowerCase();
    return resources.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.role.toLowerCase().includes(q) ||
        (r.department || '').toLowerCase().includes(q)
    );
  }, [resources, searchQuery]);

  const sortedResources = React.useMemo(() => {
    // Selected resources first, then alphabetical
    return [...filteredResources].sort((a, b) => {
      const aSelected = selectedResourceIds.includes(String(a.id));
      const bSelected = selectedResourceIds.includes(String(b.id));
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredResources, selectedResourceIds]);

  const toggleResource = (resourceId: string) => {
    const id = String(resourceId);
    if (selectedResourceIds.includes(id)) {
      onSelect(selectedResourceIds.filter((r) => r !== id));
    } else {
      onSelect([...selectedResourceIds, id]);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect([]);
  };

  // Build trigger label
  const triggerContent = React.useMemo(() => {
    if (selectedResources.length === 0) return null;
    if (selectedResources.length === 1) return selectedResources[0].name;
    return `${selectedResources.length} resources`;
  }, [selectedResources]);

  // First selected resource for avatar
  const firstResource = selectedResources[0];

  return (
    <>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearchQuery(''); }}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-start h-8 text-xs px-3',
              !firstResource && 'text-muted-foreground',
              className
            )}
          >
            <div className="flex items-center gap-2 w-full min-w-0">
              {firstResource ? (
                <>
                  {/* Avatar(s) */}
                  <div className="flex -space-x-1.5 shrink-0">
                    {selectedResources.slice(0, 3).map((r) => (
                      <Avatar key={r.id} className="h-6 w-6 border-2 border-background">
                        <AvatarImage src={r.photoUrl} alt={r.name} />
                        <AvatarFallback className="text-[9px] font-semibold bg-primary/10 text-primary">
                          {getInitials(r.name)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {selectedResources.length > 3 && (
                      <div className="h-6 w-6 rounded-full border-2 border-background bg-muted flex items-center justify-center">
                        <span className="text-[9px] font-semibold text-muted-foreground">
                          +{selectedResources.length - 3}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Label */}
                  <span className="flex-1 text-left text-sm font-medium truncate">
                    {triggerContent}
                  </span>

                  {/* Clear */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 shrink-0 hover:bg-destructive/10 ml-auto"
                    onClick={handleClear}
                    type="button"
                    tabIndex={-1}
                  >
                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive transition-colors" />
                  </Button>
                </>
              ) : (
                <>
                  <User className="h-4 w-4 opacity-40 shrink-0" />
                  <span className="flex-1 text-left">{placeholder}</span>
                </>
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[320px] p-0"
          align="start"
          side="top"
          sideOffset={5}
          avoidCollisions={false}
        >
          <Command shouldFilter={false}>
            {/* Search bar — same style as EnhancedProjectSelect */}
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder="Search by name, role or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <CommandList className="max-h-[280px] overflow-y-auto">
              {/* Results */}
              {sortedResources.length === 0 ? (
                <CommandEmpty>
                  <div className="py-6 text-center">
                    <Users className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No resources found</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try adjusting your search
                    </p>
                  </div>
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {/* Result count meta */}
                  {searchQuery && (
                    <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground">
                      <span>
                        {sortedResources.length} resource{sortedResources.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                  )}

                  {/* Currently selected header */}
                  {!searchQuery && selectedResources.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Check className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-semibold uppercase text-muted-foreground">Selected</span>
                    </div>
                  )}

                  {sortedResources.map((resource) => {
                    const isSelected = selectedResourceIds.includes(String(resource.id));
                    return (
                      <CommandItem
                        key={resource.id}
                        value={resource.id}
                        onSelect={() => toggleResource(resource.id)}
                        className="px-3 py-2 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 w-full">
                          {/* Avatar */}
                          <div className="relative shrink-0">
                            <Avatar className="h-7 w-7 rounded-md">
                              <AvatarImage src={resource.photoUrl} alt={resource.name} />
                              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                                {getInitials(resource.name)}
                              </AvatarFallback>
                            </Avatar>
                            {isSelected && (
                              <div className="absolute -top-1 -right-1 h-3.5 w-3.5 bg-primary rounded-full flex items-center justify-center border border-background">
                                <Check className="h-2 w-2 text-primary-foreground" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate leading-tight">
                              {resource.name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate leading-tight">
                              {resource.role}
                              {resource.department ? ` · ${resource.department}` : ''}
                            </p>
                            {/* Utilisation bar */}
                            {(() => {
                              const u = calculateUtilization ? calculateUtilization(String(resource.id)) : seededUtil(String(resource.id));
                              return (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[64px]">
                                    <div
                                      className="h-full rounded-full"
                                      style={{ width: `${u}%`, backgroundColor: getUtilHexColor(u) }}
                                    />
                                  </div>
                                  <span className="text-[10px] font-medium tabular-nums" style={{ color: getUtilDarkColor(u) }}>
                                    {u}% · {getUtilLabel(u)}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Check mark (right-aligned) */}
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0 text-primary transition-opacity',
                              isSelected ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>

            {/* Footer — Show More / count */}
            <div className="border-t p-2 flex items-center justify-between">
              {selectedResources.length > 0 && (
                <span className="text-xs text-muted-foreground pl-1">
                  {selectedResources.length} selected
                </span>
              )}
              <Button
                variant="ghost"
                className="text-sm ml-auto"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  setModalOpen(true);
                }}
                type="button"
              >
                <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                Show More...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Full resource lookup modal */}
      <ResourceSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        resources={resources}
        selectedResourceIds={selectedResourceIds}
        calculateUtilization={calculateUtilization || ((id) => seededUtil(String(id)))}
        onConfirm={(ids) => {
          onSelect(ids);
          setModalOpen(false);
        }}
      />
    </>
  );
}
